import type React from 'react';

import { JsonSchemaInput } from '@ragu-components/json-schema-input';
import {
  AssignDomainArrayInput,
  assignedDomainsJsonSchema,
  OrdBulkUpdateFormButton,
  OrdTabbedForm,
  OrdFormList,
  useFormType,
  OrdIconStandard,
} from '@ragu-components/ord-components';
import { LcrUpdateFields, dataSchema } from '@ragu-components/ord-types';
import { TabbedForm, useGetIdentity } from 'react-admin';

import { OrdDescriptionInput } from 'src/components/formInputs';

import { LcrsRulesInput } from './LcrsRulesInput';

import type { OrdTabbedFormProps } from '@ragu-components/ord-components';

export const LcrsForm = (props: OrdTabbedFormProps): React.ReactElement => {
  const { create } = useFormType();
  const lcrJsonSchema = create ? dataSchema.LcrCreate : dataSchema.LcrUpdate;
  const lcrRuleJsonSchema = create ? dataSchema.LcrRuleCreate : dataSchema.LcrRuleUpdate;
  const jsonSchema = {
    ...lcrJsonSchema,
    properties: {
      rules: {
        type: 'array',
        items: { ...lcrRuleJsonSchema, additionalProperties: true },
      },
      ...assignedDomainsJsonSchema,
      ...lcrJsonSchema.properties,
    },
  } as const;

  return (
    <OrdTabbedForm sanitizeFields replaceRelationships jsonSchema={jsonSchema} {...props}>
      <TabbedForm.Tab label='resources.lcrs.operations.sections.details'>
        <OrdFormList icon={<OrdIconStandard name='lcrs' />}>
          <JsonSchemaInput source={LcrUpdateFields.name} />
          <OrdDescriptionInput source={LcrUpdateFields.description} />
        </OrdFormList>
        <LcrsDomainInput />
      </TabbedForm.Tab>
      <TabbedForm.Tab label='resources.lcrs.operations.sections.rules'>
        <OrdFormList icon={<OrdIconStandard name='lcrs' />}>
          <LcrsRulesInput />
        </OrdFormList>
      </TabbedForm.Tab>
    </OrdTabbedForm>
  );
};

const LcrsDomainInput = (): React.ReactElement => {
  const { data: identity } = useGetIdentity();
  const { create } = useFormType();
  const additionalProperties =
    create && identity?.realm_id !== undefined
      ? { defaultValue: [identity.realm_id], disabledOptions: [{ id: identity.realm_id }] }
      : undefined;
  return (
    <OrdFormList icon={<OrdIconStandard name='domains' />}>
      <AssignDomainArrayInput {...additionalProperties} />
    </OrdFormList>
  );
};

/** Form for bulk editing lcrs */
export const LcrsBulkEditForm = (): React.ReactElement => {
  const inputs = [
    {
      label: 'resources.domains.name',
      element: (
        <OrdFormList icon={<OrdIconStandard name='domains' />}>
          <AssignDomainArrayInput editMode='bulk' />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.lcrs.operations.sections.rules',
      element: (
        <OrdFormList icon={<OrdIconStandard name='lcrs' />}>
          <LcrsRulesInput />
        </OrdFormList>
      ),
    },
  ];
  return <OrdBulkUpdateFormButton inputs={inputs} />;
};
